
import { CostCategoryDetail, MainCostCategory, NavigationItemType, Project, UserRole, Company, User, ExpenseEntry, SubItemConfig, FormFieldConfig, TableColumnConfig } from './types';
import { DashboardIcon } from './components/icons/DashboardIcon';
import { ProjectsIcon } from './components/icons/ProjectsIcon';
import { CompanyIcon } from './components/icons/CompanyIcon';
import { UserIcon } from './components/icons/UserIcon';
import { SettingsIcon } from './components/icons/SettingsIcon';
import { UploadIcon } from './components/icons/UploadIcon';
import { BellIcon } from './components/icons/BellIcon';

export { MainCostCategory };

export const API_BASE_URL = '/api';

export const forceHttpsUrl = (url?: string): string | undefined => {
  if (!url || !url.startsWith('http:')) {
    return url;
  }
  // This correctly ignores data: URIs and relative paths while fixing absolute http links.
  return url.replace('http:', 'https:');
};

export const formatCurrency = (value?: number | string): string => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (typeof numValue !== 'number' || isNaN(numValue)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(numValue);
};

export const getEffectiveDate = (dateString?: string, monthString?: string): string => {
  if (!dateString) {
    if (!monthString) return '';
    return `${monthString}-01`;
  }
  if (!monthString) return dateString;

  const cleanDate = dateString.slice(0, 10);
  const cleanMonth = monthString.slice(0, 7);

  if (cleanDate.slice(0, 7) === cleanMonth) {
    return cleanDate;
  }

  const dateParts = cleanDate.split('-');
  if (dateParts.length !== 3) return dateString;

  const originalDay = parseInt(dateParts[2], 10);
  if (isNaN(originalDay)) return dateString;

  const monthParts = cleanMonth.split('-');
  if (monthParts.length !== 2) return dateString;

  const targetYear = parseInt(monthParts[0], 10);
  const targetMonth = parseInt(monthParts[1], 10);
  if (isNaN(targetYear) || isNaN(targetMonth)) return dateString;

  const daysInTargetMonth = new Date(targetYear, targetMonth, 0).getDate();
  const effectiveDay = Math.min(originalDay, daysInTargetMonth);

  return `${targetYear}-${String(targetMonth).padStart(2, '0')}-${String(effectiveDay).padStart(2, '0')}`;
};

export const formatDate = (dateString?: string, monthString?: string): string => {
  if (!dateString && !monthString) return 'N/A';
  const effectiveDate = getEffectiveDate(dateString, monthString);
  if (!effectiveDate) return 'N/A';
  const date = new Date(`${effectiveDate}T12:00:00Z`);
  return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

export const formatMonthForInput = (monthString: string | undefined): string => {
  if (!monthString) {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }
  // Attempts to parse "Mês/Ano" like "Julho/2024"
  const monthYearParts = monthString.split('/');
  if (monthYearParts.length === 2) {
    const [monthPart, yearPart] = monthYearParts;
    const months: { [key: string]: string } = {
      'janeiro': '01', 'fevereiro': '02', 'março': '03', 'abril': '04', 'maio': '05', 'junho': '06',
      'julho': '07', 'agosto': '08', 'setembro': '09', 'outubro': '10', 'novembro': '11', 'dezembro': '12'
    };
    const monthNumber = months[monthPart.trim().toLowerCase()];
    if (monthNumber && !isNaN(parseInt(yearPart))) {
      return `${yearPart}-${monthNumber}`;
    }
  }

  // Fallback for "YYYY-MM" or "YYYY/MM"
  if (/^\d{4}[\/-]\d{2}$/.test(monthString)) {
    return monthString.replace('/', '-');
  }

  // Default to current month if parsing fails
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

export const formatMonthForDisplay = (monthString: string | undefined): string => {
  if (!monthString || !/^\d{4}-\d{2}$/.test(monthString)) {
    return monthString || "N/A";
  }
  const [year, month] = monthString.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, 15); // Use mid-month to avoid timezone issues
  const monthName = date.toLocaleString('pt-BR', { month: 'long' });
  return `${monthName.charAt(0).toUpperCase() + monthName.slice(1)}/${year}`;
};

export const getProjectStatusLabel = (status: 'aberta' | 'fechada' | undefined): string => {
  if (!status) return 'N/A';
  switch (status) {
    case 'aberta': return 'Ativa';
    case 'fechada': return 'Fechada';
    default: return 'N/A';
  }
};


export const ADMIN_PASSWORD = '123';

export const NAVIGATION_ITEMS: NavigationItemType[] = [
  { name: 'Dashboard', path: '/dashboard', icon: DashboardIcon, allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.DEV] },
  { name: 'Obras', path: '/obras', icon: ProjectsIcon, allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.DEV] },
  { name: 'Lembretes', path: '/lembretes', icon: BellIcon, allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.DEV] },
  { name: 'Empresas', path: '/cadastrar-empresas', icon: CompanyIcon, allowedRoles: [UserRole.ADMIN, UserRole.DEV] },
  { name: 'Funcionários', path: '/cadastrar-usuario', icon: UserIcon, allowedRoles: [UserRole.ADMIN, UserRole.DEV] },
  { name: 'Configurações', path: '/configuracoes', icon: SettingsIcon, allowedRoles: [UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.DEV] },
  { name: 'Importação', path: '/importacao', icon: UploadIcon, allowedRoles: [UserRole.DEV] },
];

const cdoSubItems = [
  { id: "cdo_ferramentas", name: "Aquisição de Ferramenta e Equipamento" },
  { id: "cdo_epc", name: "EPC´s" },
  { id: "cdo_epi", name: "EPI´s" },
  { id: "cdo_folha_obra", name: "Folha de Pagamento Obra" },
  { id: "cdo_frete", name: "Frete Direto" },
  { id: "cdo_andaimes", name: "Locação de Andaimes e Balancins" },
  { id: "cdo_loc_epc", name: "Locação de EPC" },
  { id: "cdo_loc_equip", name: "Locação de Equipamentos e Containers" },
  { id: "cdo_loc_veiculos", name: "Locação de Veículos" },
  { id: "cdo_material_aplicado", name: "Material Aplicado" },
  { id: "cdo_material_consumo", name: "Material de Consumo Obra" },
  { id: "cdo_refeicao", name: "Refeição Obra" },
  { id: "cdo_servico_terceirizado", name: "Serviço Terceirizado" },
  { id: "cdo_translados", name: "Traslados Obra" },
  { id: "cdo_treinamentos", name: "Treinamentos" },
  { id: "cdo_uniforme", name: "Uniforme Campo" },
];
const cioSubItems = [
  { id: "cio_alojamento", name: "Alojamento" },
  { id: "cio_hotel", name: "Hotel" },
  { id: "cio_convenio", name: "Convênio Médico e Clínica" },
  { id: "cio_desp_adm", name: "Despesas com ADM (Operação)" },
  { id: "cio_desp_aloj", name: "Despesas com Alojamento" },
  { id: "cio_desp_caixa", name: "Despesas com Caixa" },
  { id: "cio_energia_aloj", name: "Energia Alojamento" },
  { id: "cio_fgts", name: "FGTS" },
  { id: "cio_limpeza_aloj", name: "Limpeza Alojamento - Mão de Obra" },
  { id: "cio_manut_veiculos", name: "Manutenção e Conserto Veículos" },
  { id: "cio_mobilizacao_alojamento", name: "Mobilização de Alojamento" },
  { id: "cio_mobilizacao_canteiro", name: "Mobilização de Canteiro" },
  { id: "cio_verbas_rescis", name: "Verbas Rescisórias" },
  { id: "cio_agua_aloj", name: "Água Alojamento" },
];

export const COST_CATEGORIES: CostCategoryDetail[] = [
  {
    id: MainCostCategory.CDO,
    name: "CDO - Custo Diretos com Operação",
    subItems: cdoSubItems,
  },
  {
    id: MainCostCategory.CIO,
    name: "CIO - Custo Indireto com Operação",
    subItems: cioSubItems,
  },
  {
    id: MainCostCategory.DESPESAS_ADM,
    name: "Despesas com Administração",
    subItems: [{ id: "adm_geral_lancamento", name: "Lançamento Administrativo Direto" }],
  },
  {
    id: MainCostCategory.TRIBUTOS,
    name: "Tributos",
    subItems: [{ id: "trib_geral_lancamento", name: "Lançamento de Tributo Direto" }],
  },
  {
    id: MainCostCategory.TRIBUTOS_INSS,
    name: "Tributos - INSS Parcela Empresa",
    subItems: [{ id: "inss_geral_lancamento", name: "Lançamento de INSS Direto" }],
  },
];

// --- DYNAMIC FORM & TABLE CONFIGURATION ---
const baseFormFields: FormFieldConfig[] = [
  { name: 'supplier', label: 'Fornecedor', type: 'text', required: true, gridCols: 2 },
  { name: 'invoice', label: 'Nota Fiscal', type: 'text', required: true, gridCols: 2 },
  { name: 'date', label: 'Data do Lançamento', type: 'date', required: true, gridCols: 2 },
  { name: 'month', label: 'Mês de Competência', type: 'month', required: true, gridCols: 2 },
];
const baseTableColumns: TableColumnConfig[] = [
  { key: 'supplier', header: 'Fornecedor' },
  { key: 'invoice', header: 'Nota Fiscal' },
  { key: 'date', header: 'Data', render: (val: string, entry?: ExpenseEntry) => formatDate(val, entry?.month) },
  { key: 'amount', header: 'Valor', render: (val: number) => formatCurrency(val) },
  { key: 'actions', header: 'Ações' },
];

const withQuantityAndPrice: FormFieldConfig[] = [
  ...baseFormFields,
  { name: 'quantity', label: 'Quantidade', type: 'number', required: true, gridCols: 3 },
  { name: 'unitPrice', label: 'Valor Unitário', type: 'number', required: true, gridCols: 3 },
];

const directValueEntry: FormFieldConfig[] = [
  ...baseFormFields.filter(f => !['supplier', 'invoice'].includes(f.name as string)),
  { name: 'amount', label: 'Valor Total', type: 'amount', required: true, gridCols: 2 },
];

const withRequester: FormFieldConfig[] = [
  { name: 'requesterId', label: 'Requisitante', type: 'select_requester', required: true, gridCols: 1 },
];

const fullFormSpec: FormFieldConfig[] = [
  ...withQuantityAndPrice,
  ...withRequester,
  { name: 'description', label: 'Descrição', type: 'textarea', required: false, gridCols: 1 },
  { name: 'observation', label: 'Observação', type: 'textarea', required: false, gridCols: 1 },
  { name: 'attachments', label: 'Anexos (até 6)', type: 'attachments', required: false, gridCols: 1 },
];

const directValueWithRequesterSpec: FormFieldConfig[] = [
  ...baseFormFields,
  { name: 'amount', label: 'Valor Total', type: 'amount', required: true, gridCols: 1 },
  ...withRequester,
  { name: 'description', label: 'Descrição', type: 'textarea', required: false, gridCols: 1 },
  { name: 'observation', label: 'Observação', type: 'textarea', required: false, gridCols: 1 },
  { name: 'attachments', label: 'Anexos (até 6)', type: 'attachments', required: false, gridCols: 1 },
];

const cioGenericFormFields: FormFieldConfig[] = [
  { name: 'supplier', label: 'Fornecedor', type: 'text', required: true, gridCols: 2 },
  { name: 'requesterId', label: 'Requisitante', type: 'select_requester', required: true, gridCols: 1 },
  { name: 'invoice', label: 'Nota Fiscal', type: 'text', required: true, gridCols: 1 },
  { name: 'date', label: 'Data do Lançamento', type: 'date', required: true, gridCols: 1 },
  { name: 'month', label: 'Mês de Competência', type: 'month', required: true, gridCols: 1 },
  { name: 'amount', label: 'Valor', type: 'amount', required: true, gridCols: 3 },
  { name: 'description', label: 'Descrição', type: 'textarea', required: false, gridCols: 3 },
  { name: 'observation', label: 'Observação', type: 'textarea', required: false, gridCols: 3 },
  { name: 'attachments', label: 'Anexos (até 6)', type: 'attachments', required: false, gridCols: 3 },
];

const alojamientoFormFields: FormFieldConfig[] = [
  { name: 'supplier', label: 'Fornecedor', type: 'text', required: true, gridCols: 2 },
  { name: 'requesterId', label: 'Requisitante', type: 'select_requester', required: true, gridCols: 1 },
  { name: 'invoice', label: 'Nota Fiscal', type: 'text', required: true, gridCols: 1 },
  { name: 'date', label: 'Data do Lançamento', type: 'date', required: true, gridCols: 1 },
  { name: 'month', label: 'Mês de Competência', type: 'month', required: true, gridCols: 1 },
  { name: 'amount', label: 'Valor Mensal', type: 'amount', required: true, gridCols: 3 },
  { name: 'description', label: 'Descrição', type: 'textarea', required: false, gridCols: 3 },
  { name: 'observation', label: 'Observação', type: 'textarea', required: false, gridCols: 3 },
  { name: 'isRecurring', label: 'Lançamento Recorrente', type: 'boolean', required: false, gridCols: 1 },
  { name: 'recurrenceMonths', label: 'Repetir por mais (meses)', type: 'number', required: false, gridCols: 2 },
  { name: 'isInstallment', label: 'Lançamento Parcelado', type: 'boolean', required: false, gridCols: 1 },
  { name: 'installmentMonths', label: 'Número de Parcelas', type: 'number', required: false, gridCols: 2 },
  { name: 'attachments', label: 'Anexos (até 6)', type: 'attachments', required: false, gridCols: 3 },
];

const cioTableColumns: TableColumnConfig[] = [
  { key: 'supplier', header: 'Fornecedor' },
  { key: 'requesterId', header: 'Requisitante' },
  { key: 'invoice', header: 'Nota Fiscal' },
  { key: 'date', header: 'Data', render: (val: string, entry?: ExpenseEntry) => formatDate(val, entry?.month) },
  {
    key: 'month',
    header: 'Mês',
    render: (val: string, entry: ExpenseEntry) => (
      <div>
        <div>{formatMonthForDisplay(val)}</div>
        {entry.description && (
          <p className="text-xs text-gray-500 dark:text-dark-text-secondary mt-1 truncate max-w-[150px]" title={entry.description}>
            {entry.description}
          </p>
        )}
      </div>
    )
  },
  { key: 'amount', header: 'Valor', render: (val: number) => formatCurrency(val) },
  { key: 'actions', header: 'Ações' },
];

const cdoFolhaObraTableColumns: TableColumnConfig[] = [
  { key: 'requesterId', header: 'Requisitante' },
  { key: 'date', header: 'Data', render: (val: string, entry?: ExpenseEntry) => formatDate(val, entry?.month) },
  { key: 'month', header: 'Mês', render: (val: string) => formatMonthForDisplay(val) },
  { key: 'amount', header: 'Valor', render: (val: number) => formatCurrency(val) },
  { key: 'actions', header: 'Ações' },
];

const cdoFreteTableColumns: TableColumnConfig[] = [
  ...baseTableColumns,
  { key: 'contract', header: 'Contrato' },
];

const cdoLocVeiculosTableColumns: TableColumnConfig[] = [
  { key: 'date', header: 'Data', render: (val: string, entry?: ExpenseEntry) => formatDate(val, entry?.month) },
  { key: 'requesterId', header: 'Requisitante' },
  { key: 'licensePlate', header: 'Placa' },
  { key: 'description', header: 'Descrição' },
  { key: 'amount', header: 'Valor', render: (val: number) => formatCurrency(val) },
  { key: 'actions', header: 'Ações' },
];

const cdoMaterialAplicadoTableColumns: TableColumnConfig[] = [
  ...baseTableColumns,
  { key: 'quantity', header: 'Qtd.' },
  { key: 'unit', header: 'Unidade' },
];

const cdoTransladosTableColumns: TableColumnConfig[] = [
  { key: 'requesterId', header: 'Requisitante' },
  { key: 'date', header: 'Data', render: (val: string, entry?: ExpenseEntry) => formatDate(val, entry?.month) },
  { key: 'month', header: 'Mês', render: (val: string) => formatMonthForDisplay(val) },
  { key: 'amount', header: 'Valor', render: (val: number) => formatCurrency(val) },
  { key: 'actions', header: 'Ações' },
];

const mobilizacaoTableColumns: TableColumnConfig[] = [
  { key: 'supplier', header: 'Fornecedor' },
  { key: 'invoice', header: 'Nota Fiscal / Recibo' },
  {
    key: 'description',
    header: 'Descrição',
    render: (val: string) => (
      <span className="text-sm text-gray-800 dark:text-dark-text-primary line-clamp-2 max-w-xs block font-medium" title={val || ''}>
        {val || '-'}
      </span>
    )
  },
  { key: 'date', header: 'Data', render: (val: string, entry?: ExpenseEntry) => formatDate(val, entry?.month) },
  { key: 'amount', header: 'Valor', render: (val: number) => formatCurrency(val) },
  { key: 'actions', header: 'Ações' },
];

const mobilizacaoFormFields: FormFieldConfig[] = [
  { name: 'supplier', label: 'Fornecedor', type: 'text', required: true, gridCols: 2 },
  { name: 'invoice', label: 'Nota Fiscal / Recibo', type: 'text', required: false, gridCols: 2 },
  { name: 'date', label: 'Data do Lançamento', type: 'date', required: true, gridCols: 2 },
  { name: 'month', label: 'Mês de Competência', type: 'month', required: true, gridCols: 2 },
  { name: 'amount', label: 'Valor Total (R$)', type: 'amount', required: true, gridCols: 2 },
  { name: 'requesterId', label: 'Requisitante', type: 'select_requester', required: true, gridCols: 2 },
  { name: 'description', label: 'Descrição', type: 'textarea', required: false, gridCols: 1 },
  { name: 'observation', label: 'Observação Adicional', type: 'textarea', required: false, gridCols: 1 },
  { name: 'attachments', label: 'Anexos (até 6 comprovantes, NFs ou fotos)', type: 'attachments', required: false, gridCols: 1 },
];

export const SUB_ITEM_FORM_CONFIG: Record<string, SubItemConfig> = {
  // --- CDO ---
  'cdo_ferramentas': { formFields: fullFormSpec, tableColumns: baseTableColumns },
  'cdo_epc': { formFields: directValueWithRequesterSpec, tableColumns: baseTableColumns },
  'cdo_epi': { formFields: fullFormSpec, tableColumns: baseTableColumns },
  'cdo_folha_obra': {
    formFields: [
      { name: 'requesterId', label: 'Requisitante', type: 'select_requester', required: true, gridCols: 3 },
      { name: 'date', label: 'Data do Lançamento', type: 'date', required: true, gridCols: 1 },
      { name: 'month', label: 'Mês de Competência', type: 'month', required: true, gridCols: 1 },
      { name: 'amount', label: 'Valor', type: 'amount', required: true, gridCols: 1 },
      { name: 'attachments', label: 'Anexos (até 6)', type: 'attachments', required: false, gridCols: 3 },
    ],
    tableColumns: cdoFolhaObraTableColumns,
  },
  'cdo_frete': {
    formFields: [
      ...directValueWithRequesterSpec,
      { name: 'contract', label: 'Pedido/Contrato', type: 'text', required: false, gridCols: 2 },
      { name: 'contact', label: 'Contato', type: 'text', required: false, gridCols: 2 },
    ],
    tableColumns: cdoFreteTableColumns,
  },
  'cdo_andaimes': {
    formFields: [
      ...directValueWithRequesterSpec,
      { name: 'contact', label: 'Contato', type: 'text', required: false, gridCols: 1 },
    ],
    tableColumns: baseTableColumns,
  },
  'cdo_loc_epc': { formFields: directValueWithRequesterSpec, tableColumns: baseTableColumns },
  'cdo_loc_equip': { formFields: fullFormSpec, tableColumns: baseTableColumns },
  'cdo_loc_veiculos': {
    formFields: [
      { name: 'date', label: 'Data do Lançamento', type: 'date', required: true, gridCols: 1 },
      { name: 'month', label: 'Mês de Competência', type: 'month', required: true, gridCols: 1 },
      { name: 'amount', label: 'Valor Total', type: 'amount', required: true, gridCols: 1 },
      { name: 'requesterId', label: 'Requisitante', type: 'select_requester', required: true, gridCols: 2 },
      { name: 'licensePlate', label: 'Placa do Veículo', type: 'text', required: false, gridCols: 1 },
      { name: 'description', label: 'Descrição', type: 'textarea', required: false, gridCols: 3 },
      { name: 'attachments', label: 'Anexos (até 6)', type: 'attachments', required: false, gridCols: 3 },
    ],
    tableColumns: cdoLocVeiculosTableColumns,
  },
  'cdo_material_aplicado': {
    formFields: [
      ...fullFormSpec,
      { name: 'unit', label: 'Unidade', type: 'select_unit', required: true, gridCols: 1 },
    ],
    tableColumns: cdoMaterialAplicadoTableColumns,
  },
  'cdo_material_consumo': { formFields: fullFormSpec, tableColumns: baseTableColumns },
  'cdo_mobilizacao_canteiro': { formFields: mobilizacaoFormFields, tableColumns: mobilizacaoTableColumns },
  'cdo_terraplenagem_preliminares': { formFields: mobilizacaoFormFields, tableColumns: mobilizacaoTableColumns },
  'cdo_refeicao': { formFields: directValueWithRequesterSpec, tableColumns: baseTableColumns },
  'cdo_servico_terceirizado': { formFields: fullFormSpec, tableColumns: baseTableColumns },
  'cdo_translados': {
    formFields: [
      { name: 'date', label: 'Data do Lançamento', type: 'date', required: true, gridCols: 1 },
      { name: 'month', label: 'Mês de Competência', type: 'month', required: true, gridCols: 1 },
      { name: 'amount', label: 'Valor Total', type: 'amount', required: true, gridCols: 1 },
      { name: 'requesterId', label: 'Requisitante', type: 'select_requester', required: true, gridCols: 3 },
      { name: 'attachments', label: 'Anexos (até 6)', type: 'attachments', required: false, gridCols: 3 },
    ],
    tableColumns: cdoTransladosTableColumns,
  },
  'cdo_treinamentos': { formFields: directValueWithRequesterSpec, tableColumns: baseTableColumns },
  'cdo_uniforme': { formFields: directValueWithRequesterSpec, tableColumns: baseTableColumns },
  // --- CIO ---
  'cio_alojamento': { formFields: alojamientoFormFields, tableColumns: cioTableColumns },
  'cio_hotel': { formFields: cioGenericFormFields, tableColumns: cioTableColumns },
  'cio_convenio': { formFields: cioGenericFormFields, tableColumns: cioTableColumns },
  'cio_desp_adm': { formFields: cioGenericFormFields, tableColumns: cioTableColumns },
  'cio_desp_aloj': { formFields: cioGenericFormFields, tableColumns: cioTableColumns },
  'cio_desp_caixa': { formFields: cioGenericFormFields, tableColumns: cioTableColumns },
  'cio_energia_aloj': { formFields: cioGenericFormFields, tableColumns: cioTableColumns },
  'cio_fgts': { formFields: cioGenericFormFields, tableColumns: cioTableColumns },
  'cio_limpeza_aloj': { formFields: cioGenericFormFields, tableColumns: cioTableColumns },
  'cio_manut_veiculos': { formFields: cioGenericFormFields, tableColumns: cioTableColumns },
  'cio_mobilizacao_alojamento': { formFields: mobilizacaoFormFields, tableColumns: mobilizacaoTableColumns },
  'cio_mobilizacao_canteiro': { formFields: mobilizacaoFormFields, tableColumns: mobilizacaoTableColumns },
  'cio_mobilizacao_obra': { formFields: mobilizacaoFormFields, tableColumns: mobilizacaoTableColumns },
  'cio_verbas_rescis': { formFields: cioGenericFormFields, tableColumns: cioTableColumns },
  'cio_agua_aloj': { formFields: cioGenericFormFields, tableColumns: cioTableColumns },
};

export const UNIT_OPTIONS = [
  { value: 'peça', label: 'Peça' },
  { value: 'galão', label: 'Galão' },
  { value: 'quilos', label: 'Quilos' },
  { value: 'metros', label: 'Metros' },
  { value: 'unidade', label: 'Unidade' },
];
